import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "@/App";
import { Bell, Check, X, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const NotificationBell = ({ user }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);
  const dropdownRef = useRef(null);
  const pollIntervalRef = useRef(null);

  // Fetch notifications via REST API
  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const response = await axiosInstance.get('/notifications');
      setNotifications(response.data.notifications);
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, [user]);

  // Get WebSocket URL - try to use a stored token or skip WS
  const getWebSocketUrl = useCallback(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = backendUrl.replace(/^https?:\/\//, '');
    
    // Try to get session token from various sources
    // Note: If cookie is httpOnly, JavaScript can't access it
    const sessionToken = document.cookie
      .split('; ')
      .find(row => row.startsWith('session_token='))
      ?.split('=')[1];
    
    if (!sessionToken || sessionToken === 'undefined') {
      return null; // Can't connect without token
    }
    
    return `${wsProtocol}://${wsHost}/api/ws/notifications?token=${sessionToken}`;
  }, []);

  // Connect to WebSocket
  const connectWebSocket = useCallback(() => {
    if (!user) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const wsUrl = getWebSocketUrl();
    
    if (!wsUrl) {
      console.log('No session token available for WebSocket, using REST polling');
      setWsConnected(false);
      return;
    }

    try {
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        console.log('Notification WebSocket connected');
        setWsConnected(true);
        // Stop polling if WS is connected
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleWebSocketMessage(data);
        } catch (e) {
          console.error('Error parsing notification message:', e);
        }
      };

      wsRef.current.onclose = () => {
        setWsConnected(false);
        // Restart polling
        startPolling();
        // Reconnect after 10 seconds
        setTimeout(() => {
          if (user) connectWebSocket();
        }, 10000);
      };

      wsRef.current.onerror = (error) => {
        console.error('Notification WebSocket error:', error);
        setWsConnected(false);
      };
    } catch (error) {
      console.error('Failed to create notification WebSocket:', error);
      setWsConnected(false);
    }
  }, [user, getWebSocketUrl]);

  const handleWebSocketMessage = useCallback((data) => {
    switch (data.type) {
      case 'connected':
        setUnreadCount(data.unread_count);
        break;
      case 'new_notification':
        setNotifications(prev => [data.notification, ...prev]);
        setUnreadCount(prev => prev + 1);
        // Show toast for live events
        if (data.notification.type === 'event_live') {
          toast.success(data.notification.title, {
            description: data.notification.message,
            action: {
              label: "Watch Now",
              onClick: () => navigate(data.notification.data?.action_url || '/')
            }
          });
        }
        break;
      default:
        break;
    }
  }, [navigate]);

  // Start REST API polling as fallback
  const startPolling = useCallback(() => {
    if (pollIntervalRef.current) return; // Already polling
    if (wsConnected) return; // WebSocket is working
    
    pollIntervalRef.current = setInterval(() => {
      fetchNotifications();
    }, 30000); // Poll every 30 seconds
  }, [wsConnected, fetchNotifications]);

  // Mark as read
  const markAsRead = async (notificationId) => {
    try {
      await axiosInstance.post(`/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await axiosInstance.post('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  // Delete notification
  const deleteNotification = async (notificationId, e) => {
    e.stopPropagation();
    try {
      await axiosInstance.delete(`/notifications/${notificationId}`);
      const notif = notifications.find(n => n.id === notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      if (notif && !notif.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      await markAsRead(notification.id);
    }
    if (notification.data?.action_url) {
      navigate(notification.data.action_url);
      setIsOpen(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Initialize notifications on mount
  useEffect(() => {
    if (user) {
      // Initial fetch
      fetchNotifications();
      
      // Try WebSocket connection
      connectWebSocket();
      
      // Set up ping interval for WebSocket
      const pingInterval = setInterval(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send('ping');
        }
      }, 30000);
      
      // Start polling as fallback (will be stopped if WS connects)
      startPolling();

      return () => {
        clearInterval(pingInterval);
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
        if (wsRef.current) {
          wsRef.current.close();
        }
      };
    }
  }, [user, connectWebSocket, fetchNotifications, startPolling]);

  // Refresh notifications when dropdown opens
  useEffect(() => {
    if (isOpen && user) {
      fetchNotifications();
    }
  }, [isOpen, user, fetchNotifications]);

  if (!user) return null;

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'event_live':
        return '🎬';
      case 'event_reminder':
        return '⏰';
      case 'ticket_purchased':
        return '🎟️';
      case 'payout_completed':
        return '💰';
      default:
        return '🔔';
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-400 hover:text-white transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-gray-900 border border-gray-700 rounded-lg shadow-xl overflow-hidden z-50" data-testid="notification-dropdown">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-700 flex items-center justify-between">
            <h3 className="text-white font-semibold">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1"
                data-testid="mark-all-read-btn"
              >
                <Check className="w-4 h-4" />
                Mark all read
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-gray-500">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="w-12 h-12 text-gray-600 mx-auto mb-2" />
                <p className="text-gray-500">No notifications yet</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`px-4 py-3 border-b border-gray-800 hover:bg-gray-800/50 cursor-pointer transition-colors group ${
                    !notification.read ? 'bg-blue-900/10' : ''
                  }`}
                  data-testid={`notification-${notification.id}`}
                >
                  <div className="flex gap-3">
                    <div className="text-2xl flex-shrink-0">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm font-medium ${!notification.read ? 'text-white' : 'text-gray-300'}`}>
                          {notification.title}
                        </p>
                        <button
                          onClick={(e) => deleteNotification(notification.id, e)}
                          className="text-gray-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="text-gray-400 text-xs mt-0.5 line-clamp-2">
                        {notification.message}
                      </p>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-gray-500 text-xs">
                          {formatTime(notification.created_at)}
                        </span>
                        {notification.data?.action_url && (
                          <span className="text-blue-400 text-xs flex items-center gap-1">
                            View <ExternalLink className="w-3 h-3" />
                          </span>
                        )}
                      </div>
                    </div>
                    {!notification.read && (
                      <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-2" />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
